import type { ProjectSnapshot } from '../application/repository'
import type { Equipment } from './models'
import type { WorkOrder } from './operations'

export type TechnicalStatus = 'ready' | 'limited' | 'diagnostics' | 'unavailable' | 'unknown'
export type ConfirmedPermission = 'ready' | 'unavailable' | 'unconfirmed'
export interface Observation {
  id: string
  equipmentId: string
  symptom: string
  description: string
  canContinue: string
  photoIds: string[]
  actor: string
  occurredAt?: string
  createdAt: string
  state: string
  workOrderId: string
}
export interface FleetFilters {
  search: string
  categoryId: string
  status: string
  season: string
  base: string
  issue: string
  overdue: boolean
  dataFilter: string
  selectedId?: string
}

export function fleetRows(d: ProjectSnapshot, filters: FleetFilters, now = Date.now()): Equipment[] {
  const search = filters.search.trim().toLocaleLowerCase('ru')
  return d.equipment.filter((e) => {
    if (e.archivedAt && e.id !== filters.selectedId) return false
    const r = readinessFor(d, e, now)
    return (!filters.categoryId || e.type === filters.categoryId || e.typeKey === filters.categoryId) &&
      matchesFleetStatus(r, filters.status) &&
      (!filters.season || (e.season || 'year_round') === filters.season) &&
      (!filters.base || e.department === filters.base) &&
      (!filters.issue || (filters.issue === 'yes' ? r.reasons.length > 0 : r.reasons.length === 0)) &&
      (!filters.overdue || (r.remaining !== null && r.remaining < 0)) &&
      (!filters.dataFilter || (filters.dataFilter === 'stale' && (r.stale || r.service === 'unknown'))) &&
      (e.plate + ' ' + e.model + ' ' + e.garageNumber).toLocaleLowerCase('ru').includes(search)
  })
}
export interface TechnicalDecision {
  id: string
  equipmentId: string
  observationId: string
  workOrderId: string
  releaseBlockIds?: string[]
  status: TechnicalStatus
  reason: string
  restrictions: string
  nextAction: string
  deadline: string
  responsibleId: string
  actor: string
  createdAt: string
}
export interface TechnicalBlock {
  id: string
  equipmentId: string
  decisionId: string
  observationId?: string
  workOrderId?: string
  reason: string
  createdAt?: string
  clearedAt?: string
  clearedByDecisionId?: string
}
export interface MeterReading {
  id: string
  equipmentId: string
  meterId: string
  meterName: string
  unit: string
  source: string
  value: number
  recordedAt: string
  actor: string
  photoIds: string[]
  comment?: string
  status: string
  reason: string
  reviewedBy: string
  reviewedAt: string
}
export interface WorkOperation {
  name: string
  done: boolean
  result: string
  deferredReason?: string
}
export interface CostEntry {
  id: string
  equipmentId: string
  workOrderId: string
  category: string
  qualification: string
  amount: number
  description: string
  source: string
  documentNumber: string
  counterparty: string
  date: string
  actor: string
  createdAt: string
}
export interface ReadinessState {
  demo?: boolean
  version: number
  meterStaleDays: number
  maxDailyHours: number
  observations: Observation[]
  decisions: TechnicalDecision[]
  blocks: TechnicalBlock[]
  readings: MeterReading[]
  costs: CostEntry[]
  downtimes: {
    id: string
    equipmentId: string
    startAt: string
    endAt: string
    decisionId: string
    releaseDecisionId: string
  }[]
  fieldShifts: { id: string; equipmentId: string; actor: string; startAt: string; endAt: string }[]
  refuelings: {
    id: string
    equipmentId: string
    stockId: string
    fuelType: string
    liters: number
    comment: string
    recordedAt: string
    source: string
    photoIds: string[]
    actor: string
    createdAt: string
  }[]
  regulations: MaintenanceRegulation[]
  regulationHistory?: MaintenanceRegulation[]
}
export interface MaintenanceRegulation {
  id: string
  name: string
  scope: 'type' | 'model' | 'equipment'
  equipmentType: string
  model: string
  equipmentId: string
  serviceType: string
  trigger: 'meter' | 'calendar' | 'whichever'
  interval: number
  calendarDays: number
  warningBefore: number
  criticalBefore: number
  operations: WorkOperation[]
  materials: import('./operations').StockNeed[]
  durationHours: number
  effectiveFrom: string
  active: boolean
  createdAt: string
  updatedAt: string
  version: number
  source: string
}
const blank: ReadinessState = {
  version: 0,
  meterStaleDays: 7,
  maxDailyHours: 24,
  observations: [],
  decisions: [],
  blocks: [],
  readings: [],
  costs: [],
  downtimes: [],
  fieldShifts: [],
  refuelings: [],
  regulations: [],
}
export const readinessOf = (d: ProjectSnapshot): ReadinessState => {
  const current = { ...blank, ...(d.readiness ?? {}) }
  return {
    ...current,
    observations: Array.isArray(current.observations) ? current.observations : [],
    decisions: Array.isArray(current.decisions) ? current.decisions : [],
    blocks: Array.isArray(current.blocks) ? current.blocks : [],
    readings: Array.isArray(current.readings) ? current.readings : [],
    costs: Array.isArray(current.costs) ? current.costs : [],
    downtimes: Array.isArray(current.downtimes) ? current.downtimes : [],
    fieldShifts: Array.isArray(current.fieldShifts) ? current.fieldShifts : [],
    refuelings: Array.isArray(current.refuelings) ? current.refuelings : [],
    regulations: Array.isArray(current.regulations) ? current.regulations : [],
  }
}
export const technicalLabels: Record<TechnicalStatus, string> = {
  ready: 'Технически готова',
  limited: 'Технически готова · ограничения',
  diagnostics: 'Технически недоступна · диагностика',
  unavailable: 'Технически недоступна',
  unknown: 'Нет подтверждения',
}
export const technicalTone: Record<TechnicalStatus, string> = {
  ready: 'green',
  limited: 'amber',
  diagnostics: 'amber',
  unavailable: 'red',
  unknown: '',
}
export const workLabels: Record<string, string> = {
  planned: 'Запланирована',
  waiting: 'Ожидает ресурс',
  in_progress: 'В работе',
  control: 'Контроль',
  completed: 'Закрыта',
  cancelled: 'Отменена',
}
export const costLabels: Record<string, string> = {
  ownership: 'Владение',
  operation: 'Эксплуатация',
  maintenance: 'Плановое ТО',
  repair: 'Внеплановый ремонт',
  other: 'Прочие расходы',
}
export const costQualificationOptions: Record<string, { value: string; label: string }[]> = {
  ownership: [
    { value: 'lease', label: 'Лизинг / аренда' },
    { value: 'insurance', label: 'Страхование' },
    { value: 'tax', label: 'Налоги и сборы' },
    { value: 'depreciation', label: 'Амортизация' },
  ],
  operation: [
    { value: 'fuel', label: 'Топливо' },
    { value: 'lubricants', label: 'Масла и технические жидкости' },
    { value: 'transport', label: 'Перебазировка / доставка' },
    { value: 'washing', label: 'Мойка и эксплуатационное обслуживание' },
  ],
  maintenance: [
    { value: 'maintenance_parts', label: 'Материалы планового ТО' },
    { value: 'maintenance_labor', label: 'Работы планового ТО' },
    { value: 'diagnostics', label: 'Диагностика' },
  ],
  repair: [
    { value: 'repair_parts', label: 'Запчасти для ремонта' },
    { value: 'repair_labor', label: 'Ремонтные работы' },
    { value: 'contractor', label: 'Услуги подрядчика' },
  ],
  other: [
    { value: 'penalty', label: 'Штрафы и простои' },
    { value: 'other', label: 'Прочие расходы' },
  ],
}
export const costQualificationLabels = Object.fromEntries(
  Object.values(costQualificationOptions)
    .flat()
    .map((item) => [item.value, item.label]),
) as Record<string, string>
export const openWork = (w: WorkOrder) => w.status !== 'completed' && w.status !== 'cancelled'
export const money = (v: number) =>
  v.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 })
export function workResources(d: ProjectSnapshot, w: WorkOrder) {
  return (Array.isArray(w.materials) ? w.materials : []).map((line) => {
    const stock = d.consumables.find((s) => s.id === line.stockId)
    const confirmed = (w.reservedMaterials ?? [])
      .filter((reserved) => reserved.stockId === line.stockId)
      .reduce((total, reserved) => total + reserved.quantity, 0)
    const issued = (Array.isArray(d.movements) ? d.movements : [])
      .filter((movement) => movement.workOrderId === w.id && movement.stockId === line.stockId && movement.quantity < 0)
      .reduce((total, movement) => total - movement.quantity, 0)
    const reserved = Math.max(0, confirmed - issued)
    const free = Math.max(0, (stock?.available ?? 0) - (stock?.reserved ?? 0))
    const unverified = !!w.regulationUnverified || !stock || !!stock.sourceIssue || stock.stockMode !== 'local'
    const missing = Math.max(0, line.quantity - issued - reserved - free)
    return {
      ...line,
      name: stock?.name ?? 'Ресурс не найден',
      unit: stock?.unit ?? '',
      available: free,
      missing,
      confirmed: reserved,
      issued,
      ready: !unverified && issued + reserved >= line.quantity,
      unverified,
      source: stock?.source || 'Локальный справочник',
      sourceUpdatedAt: stock?.sourceUpdatedAt || '',
      stockMode: stock?.stockMode || (stock ? 'local' : 'none'),
    }
  })
}

export function materialCoverage(d: ProjectSnapshot, days: 7 | 14 | 30, now = Date.now()) {
  const groups = new Map<string, { stockId: string; name: string; unit: string; required: number; secured: number; free: number; missing: number | null; workCount: number }>()
  for (const work of d.workOrders) {
    if (!work.technical || !openWork(work) || work.materialMode === 'none' || !work.startAt) continue
    const due = Date.parse(work.startAt)
    if (!Number.isFinite(due) || due > now + days * 86400000) continue
    for (const line of workResources(d, work)) {
      const key = `${line.stockId}:${line.unit}`
      const group = groups.get(key) ?? {
        stockId: line.stockId, name: line.name, unit: line.unit,
        required: 0, secured: 0, free: line.available, missing: line.unverified ? null : 0, workCount: 0,
      }
      group.required += line.quantity
      group.secured += Math.min(line.quantity, line.issued + line.confirmed)
      group.workCount++
      if (line.unverified) group.missing = null
      groups.set(key, group)
    }
  }
  return [...groups.values()].map((group) => ({
    ...group,
    missing: group.missing == null ? null : Math.max(0, group.required - group.secured - group.free),
  })).sort((a, b) => (b.missing ?? -1) - (a.missing ?? -1) || a.name.localeCompare(b.name))
}

export function maintenanceForecast(
  readings: MeterReading[],
  equipmentId: string,
  remaining: number | null,
  now = Date.now(),
): string | null {
  if (remaining == null || remaining <= 0) return null
  const accepted = readings
    .filter((reading) =>
      reading.equipmentId === equipmentId &&
      (!reading.meterId || reading.meterId === 'primary') &&
      reading.status === 'accepted' &&
      Number.isFinite(reading.value) &&
      Date.parse(reading.recordedAt) >= now - 30 * 86400000 &&
      Date.parse(reading.recordedAt) <= now,
    )
    .sort((a, b) => Date.parse(a.recordedAt) - Date.parse(b.recordedAt))
  if (accepted.length < 2) return null
  const first = accepted[0]
  const last = accepted[accepted.length - 1]
  const elapsedDays = (Date.parse(last.recordedAt) - Date.parse(first.recordedAt)) / 86400000
  const increment = last.value - first.value
  if (elapsedDays < 1 || increment <= 0) return null
  const estimate = Date.parse(last.recordedAt) + (remaining / (increment / elapsedDays)) * 86400000
  if (!Number.isFinite(estimate)) return null
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(estimate))
}

export function readinessFor(d: ProjectSnapshot, e: Equipment, now = Date.now()) {
  const r = readinessOf(d)
  const activeBlocks = r.blocks.filter((block) => block.equipmentId === e.id && !block.clearedAt)
  const decisions = r.decisions.filter((x) => x.equipmentId === e.id)
  const decision = decisions[decisions.length - 1]
  const expired =
    e.technicalStatus === 'limited' && !!decision?.deadline && Date.parse(decision.deadline) < now
  const stopReport = r.observations.some(
    (o) => o.equipmentId === e.id && o.state === 'new' && o.canContinue === 'no',
  )
  // An unreviewed stop report invalidates the displayed permission, not the stored mechanic decision.
  const status: TechnicalStatus = activeBlocks.length
    ? 'unavailable'
    : expired || stopReport
      ? 'unknown'
      : e.technicalStatus || 'unknown'
  const permission: ConfirmedPermission = status === 'ready' || status === 'limited'
    ? 'ready'
    : status === 'unavailable' || status === 'diagnostics'
      ? 'unavailable'
      : 'unconfirmed'
  const rule = r.regulations.find((x) => x.id === e.regulationId && x.active)
  const usesMeter = !!rule && (rule.trigger === 'meter' || rule.trigger === 'whichever')
  const usesCalendar = rule?.trigger === 'calendar' || rule?.trigger === 'whichever'
  const stale = !e.readingAt || now - Date.parse(e.readingAt) > r.meterStaleDays * 86400000
  const meterRemaining = usesMeter && e.baselineConfirmed && e.due > 0 ? e.due - e.meter : null
  const calendarRemaining =
    usesCalendar && e.serviceDueDate
      ? Math.ceil((Date.parse(e.serviceDueDate + 'T00:00:00') - now) / 86400000)
      : null
  const level = (value: number | null) =>
    value == null || Number.isNaN(value)
      ? 'unknown'
      : value < 0
        ? 'overdue'
        : value === 0
          ? 'due'
          : value <= (e.criticalBefore || Math.max(1, e.alertBefore / 2))
            ? 'critical'
            : value <= e.alertBefore
              ? 'soon'
              : 'normal'
  const rank: Record<string, number> = { overdue: 5, due: 4, critical: 3, soon: 2, normal: 1, unknown: 0 }
  const meterService = level(meterRemaining)
  const calendarService = level(calendarRemaining)
  const calendarIsEarlier = rank[calendarService] > rank[meterService]
  const service = calendarIsEarlier ? calendarService : meterService
  const remaining = calendarIsEarlier ? calendarRemaining : meterRemaining
  const serviceUnit = calendarIsEarlier ? 'дн.' : e.meterUnit || 'мч'
  const serviceDue = calendarIsEarlier ? 'календарного ТО' : e.dueType
  const forecastDate = calendarIsEarlier
    ? null
    : maintenanceForecast(r.readings, e.id, meterRemaining, now)
  const issues = r.observations.filter((x) => x.equipmentId === e.id && x.state !== 'closed')
  const works = d.workOrders.filter((w) => w.equipmentId === e.id && openWork(w))
  const assignment = d.scheduleAssignments.find(
    (a) =>
      a.equipmentId === e.id &&
      a.status !== 'cancelled' &&
      a.status !== 'completed' &&
      Date.parse(a.endAt) > now,
  )
  const conflict = !!assignment && status !== 'ready'
  const blocked = works.some(
    (w) => w.status === 'waiting' || workResources(d, w).some((r) => r.missing > 0 || r.unverified),
  )
  const responsible = d.workers.find((w) => w.id === e.responsibleId)?.name ?? 'Не назначен'
  const reasons = [
    ...activeBlocks.map((block) => `Активный запрет: ${block.reason || 'основание не указано'}`),
    stopReport ? 'Оператор сообщил: продолжать работу нельзя. Требуется новая оценка' : '',
    conflict ? 'Есть назначение при неподтверждённой готовности' : '',
    ['unavailable', 'diagnostics', 'limited'].includes(status)
      ? e.technicalReason || technicalLabels[status]
      : '',
    issues.length ? `${issues.length} открытых сообщений о неисправности` : '',
    service === 'overdue'
      ? `ТО просрочено на ${Math.abs(remaining!).toLocaleString('ru-RU')} ${serviceUnit}`
      : '',
    service === 'due' ? `${serviceDue} наступило` : '',
    service === 'critical' ? `Критично: до ${serviceDue} ${remaining} ${serviceUnit}` : '',
    blocked ? 'Работа не обеспечена ресурсами' : '',
    works.some((w) => w.awaitingConfirmation) ? 'Работы выполнены — ожидается подтверждение готовности' : '',
    service === 'soon' ? `До ${serviceDue}: ${remaining} ${serviceUnit}` : '',
    expired ? 'Истёк срок ограничения' : '',
    !rule ? 'Нет подтверждённого регламента ТО' : '',
    stale ? 'Нет актуального показания' : '',
    status === 'unknown' ? 'Готовность не подтверждена руководителем механизации' : '',
  ].filter(Boolean) as string[]
  const next = issues.some((i) => i.state === 'new')
    ? 'Оценить неисправность'
    : blocked
      ? 'Обеспечить техническую работу'
      : works.some((w) => w.awaitingConfirmation)
        ? 'Подтвердить результат и готовность'
        : works.length
          ? 'Продолжить техническую работу'
          : ['overdue', 'due', 'critical', 'soon'].includes(service)
            ? d.workOrders.some((w) => w.equipmentId === e.id && w.kind === 'maintenance' && openWork(w))
              ? 'Назначить исполнителя и окно'
              : 'Проверить регламент ТО'
            : !rule
              ? 'Задать регламент ТО'
              : stale
              ? 'Запросить показание'
              : status !== 'ready'
                ? 'Принять техническое решение'
                : 'Открыть техническую историю'
  const priority =
    conflict || status === 'unavailable'
      ? 0
      : issues.length || service === 'overdue' || service === 'due'
        ? 1
        : blocked || service === 'critical' || service === 'soon'
          ? 2
          : 3
  return {
    status,
    permission,
    activeBlocks,
    decision,
    stale,
    remaining,
    meterRemaining,
    calendarRemaining,
    serviceUnit,
    serviceDue,
    forecastDate,
    service,
    issues,
    works,
    assignment,
    conflict,
    blocked,
    responsible,
    reasons,
    next,
    priority,
    attention: reasons.length > 0,
  }
}

export function fleetReadinessSummary(d: ProjectSnapshot, now = Date.now()) {
  const active = d.equipment.filter((e) => !e.archivedAt)
  const counts = { ready: 0, unavailable: 0, unconfirmed: 0 }
  for (const equipment of active) counts[readinessFor(d, equipment, now).permission]++
  return { ...counts, total: active.length, percent: active.length ? Math.round(counts.ready / active.length * 100) : null }
}

export function matchesFleetStatus(
  readiness: Pick<ReturnType<typeof readinessFor>, 'status' | 'attention' | 'service' | 'works'> & { permission?: ConfirmedPermission },
  filter: string,
) {
  return (
    !filter ||
    readiness.status === filter ||
    (filter === 'ready' && readiness.permission === 'ready') ||
    (filter === 'unavailable' && readiness.permission === 'unavailable') ||
    (filter === 'unconfirmed' && readiness.permission === 'unconfirmed') ||
    (filter === 'attention' && readiness.attention) ||
    (filter === 'service' && ['soon', 'overdue'].includes(readiness.service)) ||
    (filter === 'repair' &&
      (readiness.status === 'unavailable' || readiness.works.some((work) => work.kind === 'repair')))
  )
}

export function costsFor(d: ProjectSnapshot, equipmentId: string, from: string, to: string) {
  const r = readinessOf(d)
  const periodEntries = r.costs.filter(
    (c) => (!equipmentId || c.equipmentId === equipmentId) && c.date >= from && c.date <= to,
  )
  const completed = new Set(d.workOrders.filter((w) => w.status === 'completed').map((w) => w.id))
  const eligible = new Set(['maintenance_parts', 'repair_parts', 'contractor'])
  const entries = periodEntries.filter((c) => c.workOrderId && completed.has(c.workOrderId) && eligible.has(c.qualification))
  const otherEntries = periodEntries.filter((c) => !entries.includes(c))
  const total = entries.reduce((s, c) => s + c.amount, 0)
  const readings = r.readings
    .filter((x) => x.equipmentId === equipmentId && x.status === 'accepted')
    .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt))
  const before = readings.filter((x) => x.recordedAt.slice(0, 10) < from).at(-1)
  const after = readings.filter((x) => x.recordedAt.slice(0, 10) <= to).at(-1)
  const startDate = Date.parse(from + 'T00:00:00Z')
  const previousDay = Number.isFinite(startDate)
    ? new Date(startDate - 86400000).toISOString().slice(0, 10)
    : ''
  const hours =
    before &&
    after &&
    before.recordedAt.slice(0, 10) === previousDay &&
    after.recordedAt.slice(0, 10) === to &&
    after.value > before.value
      ? after.value - before.value
      : null
  const missing: string[] = []
  if (otherEntries.length) missing.push(`${otherEntries.length} расходов вне стоимости ТОиР`)
  if (
    d.workOrders.some(
      (w) =>
        (!equipmentId || w.equipmentId === equipmentId) &&
        w.status === 'completed' &&
        !w.costComplete &&
        w.completedAt.slice(0, 10) >= from &&
        w.completedAt.slice(0, 10) <= to,
    )
  )
    missing.push('Неполные цены материалов или внешних услуг')
  return {
    entries,
    otherEntries,
    total,
    hours,
    perUnit: hours && entries.length > 0 ? total / hours : null,
    missing,
    hasData: entries.length > 0,
  }
}
